import parse, {
  attributesToProps,
  type DOMNode,
  domToReact,
  type Element,
  type HTMLReactParserOptions,
} from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import {
  ComputeExpressionReferenceNode,
  parseDataReference,
} from '../../../schema/expressions/compute/input/nodes/ComputeExpressionReferenceNode.tsx';
import { InfoIcon } from '../../../ui/info-icon/InfoIcon.tsx';
import { ComputeReferenceNodeRender } from './nodes/ComputeReferenceNodeRender.tsx';
import { TooltipVirtualNode } from './nodes/TooltipVirtualNode.tsx';

/**
 * Controlla se il nodo passato è un elemento HTML.
 * 1. Non possiamo usare `node instanceof Element` per via dei test su storybook
 * 2. Al contrario, per produzione, non possiamo usare
 *    `node.constructor.name === 'Element'` altrimenti dopo la minificazione il
 *    nome della classe cambia.
 *
 * In definitiva utilizziamo un check sul `type` dell'elemento e sulla
 * presenza degli `attribs`, come suggerito anche sulla repo.
 * https://github.com/remarkablemark/html-react-parser/issues/633
 */
export const isElement = (node: DOMNode): node is Element =>
  node.type === 'tag' && 'attribs' in node;

/**
 * Renderizza un HTML avanzato, con la possibilità di sostituire alcuni nodi
 * con componenti React dove necessario.
 * Viene utilizzato per la visualizzazione di testi avanzati (creati con l'editor
 * RTE avanzato), ma può essere utilizzato anche per la visualizzazione di
 * testi HTML generici.
 */
export function renderRichTextAdvancedNodes(
  text: string | undefined | null,
  preview = false,
) {
  if (!text) return null;

  const options: HTMLReactParserOptions = {
    replace: node => {
      // Tooltip
      if (
        isElement(node) &&
        node.attribs['data-type'] === TooltipVirtualNode.name
      ) {
        return <InfoIcon text={parse(node.attribs['data-content'], options)} />;
      }

      // Reference
      if (
        isElement(node) &&
        node.attribs['data-type'] === ComputeExpressionReferenceNode.name
      ) {
        const reference = node.attribs['data-reference'];
        if (!reference) return null;
        const parsedReference = parseDataReference(reference);
        if (!parsedReference) return null;
        return (
          <ComputeReferenceNodeRender
            reference={parsedReference}
            preview={preview}
          />
        );
      }

      // Link (a11y)
      if (isElement(node) && node.name === 'a') {
        return <LinkNode node={node} options={options} />;
      }
    },
  };

  return parse(text, options);
}

function LinkNode({
  node,
  options,
}: {
  node: Element;
  options: HTMLReactParserOptions;
}) {
  const props = attributesToProps(node.attribs);
  const { t } = useTranslation();

  return (
    <a {...props}>
      {domToReact(node.children, options)}
      <span className="sr-only">
        {t(
          'inputs.rich-text-advanced-link-node.screen-reader-content',
          '(Si apre in una nuova finestra)',
        )}
      </span>
    </a>
  );
}
